import { Component, ViewEncapsulation, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Site } from '@app/models/site';
import { SiteService } from '@app/api/site.service';
import { BasketService } from '@app/api/basket.service';
import { Basket } from '@app/models/basket';
import { SEOService } from '@app/shared/services/seo.service';
import { EscapeHtmlPipe } from '@app/modules/pipes/escape-html.pipe';
import { OrderOccasion } from '@app/models/order-occasion';
import { CustomHtmlSection } from '@app/models/custom-html-section';
import { AndroWebCoreComponent } from '@app/core/AndroWebCoreComponent';
import { ServiceHelper } from '@app/shared/services/service.helper';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { NavigatorService } from '@app/core/navigator.service';
import { NavigationSectionType } from '@app/models/navigation/navigation-section-type';
import { MatDialogConfig } from '@angular/material/dialog';
import { lastValueFrom, takeUntil } from 'rxjs';
import { BackgroundImageService } from '@app/shared/services/background-image/background-image.service';
import { MarketingCarousel } from '@app/models/MarketingCarousel';
import { VisualAsset } from '@app/models/shared/visual-asset';
import { StructuredDataService } from '@app/shared/services/structured-data/structured-data.service';

/**
 * The home component where users initially land on Androweb
 * @export
 * @class HomeComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-home',
  styleUrls: ['./home.component.scss'],
  templateUrl: './home.component.html',
  standalone: false
})
export class HomeComponent extends AndroWebCoreComponent implements OnInit, OnDestroy {
  @ViewChild('confirmWantedTime', { static: true }) private confirmWantedTimeModal: TemplateRef<any>;

  public loadingSites: boolean = true;
  public noStoresFound: boolean;
  public isLoadingModal: boolean;
  public supportsDelivery: boolean;
  public supportsCollection: boolean;
  public basket: Basket;
  public allSites: Site[] = [];
  public topHtmlSection: CustomHtmlSection;
  public socialMediaSection: CustomHtmlSection;
  public estimatedDeliveryTime: string;
  public estimatedCollectionTime: string;
  public selectedTabIndex: number = 0;
  public orderOccasion: typeof OrderOccasion = OrderOccasion;
  public selectedStore: Site;

  private seoSet: boolean;
  public backgroundImage: string;
  public carouselSlides: MarketingCarousel[] = [];

  constructor(
    private router: Router,
    private seoService: SEOService,
    private siteService: SiteService,
    private navigatorService: NavigatorService,
    private serviceHelper: ServiceHelper,
    private basketService: BasketService,
    private escapeHtmlPipe: EscapeHtmlPipe,
    private structuredDataService: StructuredDataService,
    private backgroundImageService: BackgroundImageService
  ) {
    super();
    this.updatePageSEO();
    this.setCustomHtmlSections();
  }

  ngOnInit() {
    this.backgroundImageService
      .getBackgroundImage('landing-landscape', 'landing-portrait', this.tenant.Branding?.HeroImageUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe((url: string) => {
        this.backgroundImage = url;
      });

    this.siteService
      .getSites()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sites: Site[]) => {
        this.allSites = sites;
        this.loadingSites = false;
        this.supportsDelivery = this.allSites.some((x: Site) => x.OccasionsSupported?.includes(OrderOccasion.Delivery));
        this.supportsCollection = this.allSites.some((x: Site) => x.OccasionsSupported?.includes(OrderOccasion.Collection));
        this.setFastestOccasionTime();
        this.buildTenantStructuredData(sites);
      });

    const marketingCarousel = this.tenant.Branding?.VisualAssets?.filter((x: VisualAsset) => x.Type === 'marketing-carousel');
    this.carouselSlides = marketingCarousel?.map((x: VisualAsset) => ({
      Url: x.Slices[0].Url,
      Title: x.Title,
      Subtext: x.Subtext
    }));
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    // restores the page title and meta description back to the original state only if it's been edited
    if (this.seoSet) {
      this.seoService.restore();
    }
  }

  /**
   *
   * @param $event
   */
  public onNoStoresFound($event: boolean): void {
    if ($event) {
      this.selectedTabIndex = 1;
    }

    this.noStoresFound = $event;
  }

  /**
   * Opens the confirm wanted time modal and gets the users current basket
   * @param occasion - the order occasion
   * @param selectedStore - the selected store (optional)
   */
  public async openConfirmWantedTimeModal(occasion: OrderOccasion, selectedStore: Site, postcode?: string): Promise<void> {
    if (!selectedStore) {
      return;
    }

    this.isLoadingModal = true;
    this.selectedStore = selectedStore;

    const basket: Basket = await lastValueFrom(this.basketService.getCurrentBasketBySiteId(this.selectedStore.Id, occasion, null));

    if (basket) {
      this.basket = basket;

      if (!basket.IsModifiable) {
        this.trackTrace('unmodifiable basket on home page', SeverityLevel.Warning);
        this.basket = await this.handleUnmodifiableBasket(this.selectedStore.Id);
      }

      await this.basketService.setOccasionOfCurrentBasket(this.selectedStore.Id, occasion, postcode);

      const options: MatDialogConfig = {
        autoFocus: false,
        disableClose: true,
        width: this.isMobile ? '100%' : ''
      };
      this.openDialog(this.confirmWantedTimeModal, 'order-wanted-time-modal', options);
    } else {
      this.trackTrace('openConfirmWantedTimeModal: getCurrentBasketBySiteId() => null basket', SeverityLevel.Warning);
    }

    this.isLoadingModal = false;
  }

  /**
   * Closes the confirm wanted time modal.
   * @param $event
   */
  public onWantedTimeUpdated($event: Basket): void {
    this.basket = $event;
    const location: string = this.navigatorService.getLocationBySiteId(this.selectedStore.Id);
    const routePath: string = this.navigatorService.generateRoutePath(location, this.basket.Occasion, NavigationSectionType.Menu);
    this.router.navigate([routePath]);
  }

  /**
   * sets the custom html sections.
   * @returns
   */
  private setCustomHtmlSections(): void {
    if (!this.tenant.CustomHtmlSections) {
      return;
    }

    const top: CustomHtmlSection = this.tenant.CustomHtmlSections.find((section: CustomHtmlSection) => section.Enabled && section.DisplayOrder === 0);

    if (top) {
      this.topHtmlSection = { ...top, Content: this.escapeHtmlPipe.transform(top.Content) };
    }

    const socialMedia: CustomHtmlSection = this.tenant.CustomHtmlSections.find((x: CustomHtmlSection) => x.Enabled && x.Title.toLowerCase() === 'follow us on social media');

    if (socialMedia) {
      this.socialMediaSection = { ...socialMedia, Content: this.escapeHtmlPipe.transform(socialMedia.Content) };
    }
  }

  /**
   * Clears the current basket for the given site id and creates a new basket then reloads the page
   * @param siteId - the order occasion
   */
  private async handleUnmodifiableBasket(siteId: string): Promise<Basket> {
    return await this.basketService.clearOldAndCreateNewBasket(siteId);
  }

  /**
   * sets the fastest estimate time for delivery and collection.
   * @returns
   */
  private setFastestOccasionTime(): void {
    const openSites: Site[] = this.allSites.filter((x) => this.serviceHelper.getOpeningDatesAndTimes(x.OpeningHours).some((y) => y.open));

    if (openSites.length === 0) {
      return;
    }

    const fastestDeliveryTime = this.getFastestAvailableOccasionTime(openSites, OrderOccasion.Delivery) ?? 30;
    const fastestCollectionTime = this.getFastestAvailableOccasionTime(openSites, OrderOccasion.Collection) ?? 15;

    this.estimatedDeliveryTime = `${fastestDeliveryTime - 5}-${fastestDeliveryTime + 5} mins`;
    this.estimatedCollectionTime = `${fastestCollectionTime - 5}-${fastestCollectionTime + 5} mins`;
  }

  /**
   * adds structured data to the page.
   * see https://developers.google.com/search/docs/guides/intro-structured-data
   * @param sites
   */
  private buildTenantStructuredData(sites: Site[]): void {
    const data: string = this.structuredDataService.buildTenantStructuredData(sites);

    if (data) {
      const script: HTMLScriptElement = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.textContent = data;
      document.head.appendChild(script);
    }
  }

  /**
   * Updates the page title and meta description
   */
  private updatePageSEO(): void {
    let description: string = '{{TenantWebsiteName}} fresh food prepared daily';
    if (this.tenant?.PageSeos?.length > 0) {
      description = this.tenant.PageSeos[0].Description;
    }
    this.seoService.updateTitle('{{TenantWebsiteName}}');
    this.seoService.updateDescription(description);
  }

  /**
   * Takes in a sites array and returns the fastest order occasion time out of all sites in a number of minutes format
   * @param openSites - the sites to use
   * @param occasion - the order occasion
   */
  private getFastestAvailableOccasionTime(openSites: Site[], occasion: OrderOccasion): number {
    const times: number[] = openSites.map((site: Site) => {
      const timeString: string = occasion === OrderOccasion.Delivery ? site.EstimatedDeliveryTime : site.EstimatedCollectionTime;
      const [hours, minutes, seconds] = timeString.split(':').map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    });

    const fastestTime = Math.min(...times.filter((time) => time > 1));
    return fastestTime / 60; // Convert to minutes
  }
}
